import type { ReactNode } from 'react';
import { LatexText } from '../latex';
import { buildQuestionDisplayText, correctOptions, paperNumberLabel, unplacedFigures } from '@padcanvas/shared';
import { DetailLink, OptionChip } from '../components';
import type { OperationQuestion, OperationQuestionPart } from './types';

/**
 * Question display card — text, diagram, correct answer (with MCQ option
 * chip rendering), optional solution image. Consumed by every page that
 * shows a question in its input.
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface QuestionCardProps {
    question?: OperationQuestion;
    onViewQuestion?: (questionId: string) => void;
    /** When true, render the Correct Answer / Solution blocks. Hint pages
     *  typically hide these to avoid spoilers. */
    showAnswer?: boolean;
    /** The answer the analysis EXTRACTED from the student's work — not what they
     *  typed or selected. Shown under the correct answer so the two can be
     *  compared without scrolling to the assessment, which is where the
     *  disagreement usually shows up. Omitted ⇒ the line is not rendered. */
    studentAnswer?: string;
    /** Optional notice rendered above the question text — for saying something about
     *  the version on screen (e.g. "edited after this attempt") before it is read. */
    banner?: ReactNode;
    /** Optional trailing content (e.g. tag panel). */
    children?: ReactNode;
}

type OptionLite = NonNullable<OperationQuestion['options']>[number];

/** Match an answer STRICTLY to the option it names. Unlike `correctOptions`,
 *  it never falls back to the `isCorrect` flag — for a student's answer that
 *  fallback would return the right option no matter what they actually put. */
function findOptionByAnswer(
    options: OperationQuestion['options'],
    answer: string,
): OptionLite | undefined {
    if (!options?.length || !answer) return undefined;
    const needle = answer.trim().toLowerCase();
    return options.find(o => o.text?.trim().toLowerCase() === needle)
        || options.find(o => o.label?.trim().toLowerCase() === needle);
}

const partLabel = (p: OperationQuestionPart) => p.label || `(${p.partId})`;

/** One answer key: the correct option chip(s) when the key names options, else the text. */
function AnswerValue({ options, answer }: { options?: OperationQuestion['options']; answer?: string }) {
    const correct = correctOptions(options, answer).filter(o => o.label && o.text);
    if (correct.length) {
        return <>{correct.map(o => (
            <OptionChip key={o.label} label={o.label!} value={o.text!} color="success" />
        ))}</>;
    }
    if (!answer) return <span>-</span>;
    return <span><LatexText text={answer} /></span>;
}

export function QuestionCard({
    question,
    onViewQuestion,
    showAnswer = false,
    studentAnswer,
    banner,
    children,
}: QuestionCardProps) {
    // STRUCTURED: the stem is only the shared preamble — the sub-questions and the
    // answers that are actually marked live on the parts.
    const structuredParts = question?.questionType === 'STRUCTURED' && question.parts?.length
        ? question.parts : null;
    // The number the student's paper shows — what a teacher's report and the
    // answer sheet both use — not the question's position in the test.
    const printed = paperNumberLabel(question?.sourceQuestionNumber);
    return (
        <div className="card question-card">
            <h3>
                Question{printed ? ` ${printed}` : ''}{' '}
                {question?.id && (
                    <DetailLink
                        href={`/questions?id=${question.id}`}
                        title="View question"
                        onClick={e => {
                            e.preventDefault();
                            onViewQuestion?.(question.id!);
                        }}
                    />
                )}
            </h3>
            {banner}
            {structuredParts ? (
                <>
                    {question?.questionText && (
                        <p className="question-text"><LatexText text={question.questionText} /></p>
                    )}
                    <ol className="question-parts">
                        {structuredParts.map(p => (
                            <li key={p.partId} className="question-part">
                                <span className="question-part-label">{partLabel(p)}</span>
                                <span className="question-part-body">
                                    {p.statement ? <LatexText text={p.statement} /> : <span className="muted">—</span>}
                                    {p.marks ? <span className="question-part-marks">[{p.marks}]</span> : null}
                                </span>
                            </li>
                        ))}
                    </ol>
                </>
            ) : (
                <p className="question-text">
                    {question?.questionText
                        ? <LatexText text={buildQuestionDisplayText(question)} figures={question.figures} />
                        : 'Question not available'}
                </p>
            )}
            {question?.questionDiagramUrl && (
                <div className="question-image-wrap">
                    <img
                        src={question.questionDiagramUrl}
                        alt="Question diagram"
                        className="question-diagram-img"
                    />
                </div>
            )}
            {unplacedFigures(question?.questionText, question?.figures).map(f => (
                <div key={f.id} className="question-image-wrap">
                    <img src={f.url} alt={f.alt || 'Question figure'} className="question-diagram-img" />
                </div>
            ))}
            {showAnswer && structuredParts && (
                <div className="correct-answer correct-answer--parts">
                    <label>Correct Answer:</label>
                    <div className="correct-answer-parts">
                        {structuredParts.map(p => (
                            <div key={p.partId} className="correct-answer-part">
                                <span className="question-part-label">{partLabel(p)}</span>
                                <AnswerValue options={p.options} answer={p.correctAnswer} />
                            </div>
                        ))}
                        {/* A top-level key on a STRUCTURED question is never marked against.
                            Prompts read it only when no part states an answer — but attempts
                            graded before that rule saw it IN PLACE of the part answers, so it
                            stays visible while it is stored. */}
                        {question?.correctAnswer && (
                            <div className="correct-answer-part" data-testid="structured-toplevel-answer">
                                <span
                                    className="question-part-label"
                                    title="Stored top-level answer. Not scored. Sent to prompts only when no part states an answer; before 2026-09-23 it replaced the part answers."
                                >
                                    Whole question
                                </span>
                                <span><LatexText text={question.correctAnswer} /></span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showAnswer && !structuredParts && (
                <div className="correct-answer">
                    <label>Correct Answer:</label>
                    {question?.correctAnswer
                        ? <AnswerValue options={question.options} answer={question.correctAnswer} />
                        : <span>-</span>}
                </div>
            )}
            {showAnswer && studentAnswer && (
                <div className="correct-answer student-answer">
                    <label>Detected Answer:</label>
                    {(() => {
                        // Match it to an option when it names one, so an MCQ shows the
                        // same chip shape as the correct answer above and the two can be
                        // compared at a glance rather than read as text.
                        const picked = findOptionByAnswer(question?.options, studentAnswer);
                        if (picked?.label && picked.text) {
                            const right = correctOptions(question?.options, question?.correctAnswer)
                                .some(o => o.label === picked.label);
                            return <OptionChip label={picked.label} value={picked.text} color={right ? 'success' : 'error'} />;
                        }
                        return <span><LatexText text={studentAnswer} /></span>;
                    })()}
                </div>
            )}
            {showAnswer && question?.solutionImageUrl && (
                <div className="question-solution-wrap">
                    <label>Solution:</label>
                    <img
                        src={question.solutionImageUrl}
                        alt="Solution"
                        className="question-solution-img"
                    />
                </div>
            )}
            {children}
        </div>
    );
}
